import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ArrowRight, ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { EntityImageUpload } from '@/components/portal/common/FileUpload/EntityImageUpload';
import { useUserStore } from '@/store/useUserStore';
import { useBrandingReminderStore } from '@/store/useBrandingReminderStore';
import { api } from '@/lib/api';

// App\Enums\FileResourcePurpose
const USER_AVATAR_PURPOSE = 604;

// Stored per-user AND per-workspace in sessionStorage. Super admins can switch
// workspaces mid-session — dismissing for workspace A shouldn't suppress the
// reminder when they switch to workspace B (different logo, different org
// units). `none` covers the super-admin-without-workspace case where only the
// central avatar applies.
const dismissKey = (userId: number, workspaceId: number | null) =>
  `branding-reminder-dismissed:${userId}:${workspaceId ?? 'none'}`;

// Snooze flag scoped to the whole login session. When set, the dialog stays
// hidden regardless of which workspace the super admin switches into. Wiped
// on next login() in the user store.
const snoozeAllKey = (userId: number) =>
  `branding-reminder-snoozed-all:${userId}`;

// Persistent (localStorage) snooze — survives logout and re-login. Only cleared
// when the user clears their browser's site data.
const browserSnoozeKey = (userId: number) =>
  `branding-reminder-snoozed-browser:${userId}`;

type OrgUnitMissingLogo = {
  id: number;
  name: string;
};

// Per-slot resolution state. The dialog never renders during 'loading' — the
// Topbar shows a small spinner on the avatar instead. We only open the dialog
// once every applicable slot has resolved AND at least one is 'missing'.
type SlotStatus = 'loading' | 'missing' | 'ok' | 'na';

export const BrandingReminderDialog = () => {
  const user = useUserStore((state) => state.user);
  const role = useUserStore((state) => state.role);
  const isSuperAdmin = useUserStore((state) => state.isSuperAdmin);
  const selectedWorkspaceId = useUserStore((state) => state.selectedWorkspaceId);
  const updateProfile = useUserStore((state) => state.updateProfile);
  const updateSuperAdminAvatarUrl = useUserStore((state) => state.updateSuperAdminAvatarUrl);
  const setChecking = useBrandingReminderStore((state) => state.setChecking);

  const userId = user?.user_id;

  const canManageWorkspaceLogo = isSuperAdmin || role === 'admin';
  const canManageOrgUnits = isSuperAdmin;
  const activeWorkspaceId = isSuperAdmin ? selectedWorkspaceId : user?.workspace_id ?? null;
  const canManageAvatar = isSuperAdmin || !!activeWorkspaceId;

  const avatarValue = isSuperAdmin
    ? user?.super_admin_avatar_url ?? null
    : user?.user_profile?.avatar_url ?? null;

  const [open, setOpen] = useState(false);
  const [snoozed, setSnoozed] = useState(false);
  const [workspaceLogoUrl, setWorkspaceLogoUrl] = useState<string | null>(null);
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  const [orgUnitsMissingLogo, setOrgUnitsMissingLogo] = useState<OrgUnitMissingLogo[]>([]);
  const [avatarStatus, setAvatarStatus] = useState<SlotStatus>('loading');
  const [workspaceStatus, setWorkspaceStatus] = useState<SlotStatus>('loading');
  const [orgUnitsStatus, setOrgUnitsStatus] = useState<SlotStatus>('loading');

  // Whenever the (user, workspace) pair changes: reset everything, decide
  // whether we're snoozed, and kick off background checks.
  useEffect(() => {
    if (!userId) return;
    setOpen(false);
    setWorkspaceLogoUrl(null);
    setWorkspaceName(null);
    setOrgUnitsMissingLogo([]);
    setAvatarStatus('loading');
    setWorkspaceStatus('loading');
    setOrgUnitsStatus('loading');
    const isSnoozed =
      !!localStorage.getItem(browserSnoozeKey(userId)) ||
      !!sessionStorage.getItem(snoozeAllKey(userId)) ||
      !!sessionStorage.getItem(dismissKey(userId, activeWorkspaceId));
    setSnoozed(isSnoozed);
  }, [userId, activeWorkspaceId]);

  // Avatar resolution: derived from the user store, but we still go through a
  // 'loading' tick so the Topbar spinner has time to mean something on slow
  // devices and so all three slots resolve under a single completion signal.
  useEffect(() => {
    if (snoozed) return;
    if (!canManageAvatar) {
      setAvatarStatus('na');
      return;
    }
    setAvatarStatus('loading');
    const t = setTimeout(() => {
      setAvatarStatus(avatarValue ? 'ok' : 'missing');
    }, 250);
    return () => clearTimeout(t);
  }, [snoozed, canManageAvatar, avatarValue]);

  // Workspace logo resolution (network).
  useEffect(() => {
    if (snoozed) return;
    if (!canManageWorkspaceLogo || !activeWorkspaceId) {
      setWorkspaceStatus('na');
      return;
    }
    setWorkspaceStatus('loading');
    let cancelled = false;
    api
      .get<{ data?: { logo_url?: string | null; name?: string } }>(`/workspaces/${activeWorkspaceId}`)
      .then((response) => {
        if (cancelled) return;
        const logoUrl = response?.data?.logo_url ?? null;
        setWorkspaceLogoUrl(logoUrl);
        setWorkspaceName(response?.data?.name ?? null);
        setWorkspaceStatus(logoUrl ? 'ok' : 'missing');
      })
      .catch(() => {
        if (cancelled) return;
        // Treat fetch failure as "missing" so the user has a chance to upload,
        // but blank the URL so the upload UI starts empty.
        setWorkspaceLogoUrl(null);
        setWorkspaceStatus('missing');
      });
    return () => {
      cancelled = true;
    };
  }, [snoozed, canManageWorkspaceLogo, activeWorkspaceId]);

  // Org units resolution (list fetch).
  useEffect(() => {
    if (snoozed) return;
    if (!canManageOrgUnits) {
      setOrgUnitsStatus('na');
      return;
    }
    setOrgUnitsStatus('loading');
    let cancelled = false;
    api
      .get<{ data?: Array<{ id: number; name: string; logo_url?: string | null }> }>(
        '/organizational-units?per_page=200',
      )
      .then((response) => {
        if (cancelled) return;
        const missing = (response?.data ?? [])
          .filter((u) => !u.logo_url)
          .map((u) => ({ id: u.id, name: u.name }));
        setOrgUnitsMissingLogo(missing);
        setOrgUnitsStatus(missing.length === 0 ? 'ok' : 'missing');
      })
      .catch(() => {
        if (cancelled) return;
        setOrgUnitsMissingLogo([]);
        setOrgUnitsStatus('missing');
      });
    return () => {
      cancelled = true;
    };
  }, [snoozed, canManageOrgUnits, selectedWorkspaceId]);

  // Publish the aggregate "still checking" flag so the Topbar can render a
  // small spinner overlay on the user avatar.
  const checking =
    !snoozed &&
    (avatarStatus === 'loading' ||
      workspaceStatus === 'loading' ||
      orgUnitsStatus === 'loading');
  useEffect(() => {
    setChecking(checking);
    return () => setChecking(false);
  }, [checking, setChecking]);

  // Once everything's resolved, open the dialog if anything's missing.
  const anyMissing =
    avatarStatus === 'missing' ||
    workspaceStatus === 'missing' ||
    orgUnitsStatus === 'missing';

  useEffect(() => {
    if (snoozed) return;
    if (checking) return;
    if (!anyMissing) return;
    setOpen(true);
  }, [snoozed, checking, anyMissing]);

  // Auto-close once the user has filled everything in. Triggers when an item
  // transitions from 'missing' to 'ok' (or 'na') and no slot is left missing.
  // Small delay so the user gets a moment to register the upload before the
  // modal vanishes — the EntityImageUpload toast already confirms success.
  useEffect(() => {
    if (!open) return;
    if (checking) return;
    if (anyMissing) return;
    const t = setTimeout(() => handleDismiss(), 400);
    return () => clearTimeout(t);
    // handleDismiss is stable enough — it only reads userId/activeWorkspaceId
    // which are captured at render time. Including it would cause repeat
    // close attempts on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, checking, anyMissing]);

  const firstNameChar = user?.user_profile?.first_name?.[0];
  const userNameChar = user?.user?.name?.[0];
  const initials = (firstNameChar || userNameChar || 'U').toUpperCase();

  const persistUserAvatar = async (fileResourceId: number | null, url: string | null) => {
    const response = await api.patch<{
      data?: { avatar_url?: string | null; avatar_file_resource_id?: number | null };
    }>('/me/avatar', { avatar_file_resource_id: fileResourceId });
    updateProfile({
      avatar_file_resource_id: response?.data?.avatar_file_resource_id ?? fileResourceId,
      avatar_url: response?.data?.avatar_url ?? url,
    });
  };

  // "Skip for now" — only suppress on this (user, workspace) combo. The dialog
  // re-opens on the next workspace switch (super admin) or page reload.
  const handleDismiss = () => {
    if (userId) {
      sessionStorage.setItem(dismissKey(userId, activeWorkspaceId), '1');
    }
    setOpen(false);
  };

  // "Skip for this session" — hide for the rest of this login. sessionStorage
  // clears on tab close; the user store also wipes this key on next login().
  const handleSkipForSession = () => {
    if (userId) {
      sessionStorage.setItem(snoozeAllKey(userId), '1');
      sessionStorage.setItem(dismissKey(userId, activeWorkspaceId), '1');
    }
    setOpen(false);
  };

  // "Skip for this browser" — persistent dismissal. Survives logout/login.
  // Only cleared when the user wipes browser site data.
  const handleSkipForBrowser = () => {
    if (userId) {
      localStorage.setItem(browserSnoozeKey(userId), '1');
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleDismiss(); }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Finish setting up your profile</DialogTitle>
          <DialogDescription>
            A quick reminder — these items still need an image. You can dismiss this for now and come back later.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6 py-2 sm:flex-row sm:flex-wrap sm:items-start">
          {avatarStatus === 'missing' && (
            <div className="flex flex-col items-center gap-2">
              {isSuperAdmin ? (
                <EntityImageUpload
                  mode="direct"
                  value={avatarValue}
                  shape="circle"
                  size={88}
                  fallback={initials}
                  uploadUrl="/me/avatar"
                  deleteUrl="/me/avatar"
                  pickUrl={(r) => r?.data?.avatar_url ?? null}
                  onChanged={(url) => updateSuperAdminAvatarUrl(url)}
                />
              ) : (
                <EntityImageUpload
                  value={avatarValue}
                  shape="circle"
                  size={88}
                  fallback={initials}
                  purpose={USER_AVATAR_PURPOSE}
                  onUploaded={(id, url) => persistUserAvatar(id, url)}
                  onRemove={() => persistUserAvatar(null, null)}
                />
              )}
              <div className="text-center">
                <div className="text-sm font-medium text-gray-800">Your avatar</div>
                <div className="text-xs text-gray-500">
                  {user?.user_profile?.full_name || user?.user?.name}
                </div>
              </div>
            </div>
          )}

          {workspaceStatus === 'missing' && activeWorkspaceId && (
            <div className="flex flex-col items-center gap-2">
              <EntityImageUpload
                mode="direct"
                value={workspaceLogoUrl}
                shape="square"
                size={88}
                uploadUrl={`/workspaces/${activeWorkspaceId}/logo`}
                deleteUrl={`/workspaces/${activeWorkspaceId}/logo`}
                pickUrl={(r) => r?.data?.logo_url ?? null}
                onChanged={(url) => {
                  setWorkspaceLogoUrl(url);
                  // Workspace status doesn't depend on logo URL (re-fetching
                  // would loop), so flip it explicitly to trigger auto-close.
                  setWorkspaceStatus(url ? 'ok' : 'missing');
                }}
              />
              <div className="text-center">
                <div className="text-sm font-medium text-gray-800">Workspace logo</div>
                <div className="text-xs text-gray-500">{workspaceName ?? '—'}</div>
              </div>
            </div>
          )}

          {orgUnitsStatus === 'missing' && (
            <Link
              to="/portal/admin/organizational-units"
              onClick={handleDismiss}
              className="group flex min-w-[220px] flex-1 flex-col gap-2 rounded-lg border-2 border-dashed border-gray-300 p-3 transition-colors hover:border-pink-400 hover:bg-pink-50/40 sm:max-w-sm"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 text-purple-600">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800">Organisational units</div>
                  <div className="text-xs text-gray-500">
                    {orgUnitsMissingLogo.length} unit{orgUnitsMissingLogo.length === 1 ? '' : 's'} missing a logo
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-pink-500" />
              </div>
              <ul className="list-disc pl-9 text-xs text-gray-600 space-y-0.5 max-h-32 overflow-y-auto">
                {orgUnitsMissingLogo.slice(0, 8).map((u) => (
                  <li key={u.id} className="truncate">{u.name}</li>
                ))}
                {orgUnitsMissingLogo.length > 8 && (
                  <li className="list-none text-gray-400">+ {orgUnitsMissingLogo.length - 8} more</li>
                )}
              </ul>
            </Link>
          )}
        </div>

        <DialogFooter>
          <div className="inline-flex">
            <Button
              variant="ghost"
              onClick={handleDismiss}
              className="rounded-r-none"
            >
              Skip for now
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-l-none border-l border-gray-200 px-2"
                  aria-label="More skip options"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={handleSkipForSession}>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">Skip for this session</span>
                    <span className="text-xs text-gray-500">Hidden until you log out</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleSkipForBrowser}>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">Skip for this browser</span>
                    <span className="text-xs text-gray-500">
                      Hidden until you clear site data
                    </span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
